import { useState } from 'react';
import { Heart } from '@phosphor-icons/react';
import { vote, removeVote } from '../../utils/community';
import { useAuth } from '../../contexts/AuthContext';

interface HeartButtonProps {
  postId: string;
  upvoteCount: number;
  userVote?: 1 | -1 | null;
  onLoginRequired: () => void;
}

export default function HeartButton({ postId, upvoteCount, userVote: initialVote, onLoginRequired }: HeartButtonProps) {
  const { currentUser } = useAuth();
  const [count, setCount] = useState(upvoteCount);
  const [liked, setLiked] = useState(initialVote === 1);
  const [loading, setLoading] = useState(false);

  const handleClick = async (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();

    if (!currentUser) {
      onLoginRequired();
      return;
    }
    if (loading) return;

    setLoading(true);
    const oldCount = count;
    const oldLiked = liked;

    try {
      if (liked) {
        setLiked(false);
        setCount(count - 1);
        await removeVote(postId);
      } else {
        setLiked(true);
        setCount(count + 1);
        await vote(postId, 1);
      }
    } catch {
      setCount(oldCount);
      setLiked(oldLiked);
    } finally {
      setLoading(false);
    }
  };

  return (
    <button
      onClick={handleClick}
      className={`flex items-center gap-1.5 text-sm transition-colors ${
        liked
          ? 'text-pink-500'
          : 'text-gray-400 dark:text-gray-500 hover:text-pink-500'
      }`}
      aria-label={liked ? 'Unlike' : 'Like'}
    >
      <Heart size={18} weight={liked ? 'fill' : 'regular'} />
      <span className={liked ? 'font-medium' : ''}>{count}</span>
    </button>
  );
}
